import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Rocket, Menu, X, ChevronDown, ArrowRight } from "lucide-react";

const LINK_CLASS =
  "text-[13px] text-slate-300 hover:text-white transition-colors relative after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-[#FF8800] after:transition-all hover:after:w-full whitespace-nowrap";

const NAV = [
  { href: "#home", label: "Home", testid: "navbar-link-home" },
  { href: "#what-we-do", label: "What We Do", testid: "navbar-link-what-we-do" },
  { href: "#how-we-work", label: "How We Work", testid: "navbar-link-how-we-work" },
  { to: "/projects", label: "Projects", testid: "navbar-link-projects" },
  { to: "/registrations", label: "Registrations", testid: "navbar-link-registrations" },
  { to: "/services", label: "Services", testid: "navbar-link-services" },
  {
    label: "Insights",
    testid: "navbar-link-insights",
    children: [
      { to: "/blog", label: "Blog", testid: "navbar-link-blog" },
      { to: "/news", label: "News", testid: "navbar-link-news" },
    ],
  },
  { to: "/careers", label: "Careers", testid: "navbar-link-careers" },
  { to: "/events", label: "Events", testid: "navbar-link-events" },
  { to: "/contact", label: "Contact", testid: "navbar-link-contact" },
];

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);
  const [open, setOpen] = useState(false);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const insightsRef = useRef(null);
  const location = useLocation();
  const base = location.pathname === "/" ? "" : "/";

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
      const el = document.documentElement;
      const max = el.scrollHeight - el.clientHeight;
      setProgress(max > 0 ? Math.min(1, el.scrollTop / max) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (insightsRef.current && !insightsRef.current.contains(e.target)) setInsightsOpen(false);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  const renderLink = (l, mobile = false) => {
    const cls = mobile ? "text-sm text-slate-200 py-1 block" : LINK_CLASS;
    const testid = mobile ? `${l.testid}-mobile` : l.testid;
    if (l.to) {
      return (
        <Link key={l.to} to={l.to} data-testid={testid} onClick={() => mobile && setOpen(false)} className={cls}>
          {l.label}
        </Link>
      );
    }
    return (
      <a key={l.href} href={`${base}${l.href}`} data-testid={testid} onClick={() => mobile && setOpen(false)} className={cls}>
        {l.label}
      </a>
    );
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 glass ${
        scrolled ? "py-2 shadow-[0_10px_40px_rgba(0,0,0,0.45)]" : "py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4">
        <Link to="/" data-testid="navbar-brand-logo" className="flex items-center gap-2.5 group shrink-0">
          <div className={`rounded-lg bg-gradient-to-br from-[#FF8800] to-[#ff5e00] flex items-center justify-center transition-all duration-500 ${scrolled ? "w-8 h-8" : "w-10 h-10"} shadow-[0_0_25px_rgba(255,136,0,0.35)]`}>
            <Rocket className={`text-[#06080E] transition-all duration-500 ${scrolled ? "w-4 h-4" : "w-5 h-5"}`} strokeWidth={2.4} />
          </div>
          <span className={`font-display font-extrabold tracking-[0.18em] text-white transition-all duration-500 ${scrolled ? "text-base" : "text-lg"}`}>
            LAUNCH<span className="text-[#FF8800]">LAB</span>
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-5" data-testid="navbar-links">
          {NAV.map((l) =>
            l.children ? (
              <div key={l.label} className="relative" ref={insightsRef}>
                <button
                  data-testid={l.testid}
                  onClick={() => setInsightsOpen((v) => !v)}
                  className={`${LINK_CLASS} flex items-center gap-1`}
                  aria-expanded={insightsOpen}
                >
                  {l.label}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${insightsOpen ? "rotate-180" : ""}`} />
                </button>
                {insightsOpen && (
                  <div className="absolute top-full left-0 mt-3 glass rounded-xl py-2 min-w-[140px] shadow-[0_20px_50px_rgba(0,0,0,0.5)]" data-testid="insights-dropdown">
                    {l.children.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        data-testid={c.testid}
                        onClick={() => setInsightsOpen(false)}
                        className="block px-5 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              renderLink(l)
            )
          )}
        </nav>

        <div className="flex items-center gap-2.5 shrink-0">
          <Link
            to="/portal"
            data-testid="navbar-founder-login"
            className="hidden xl:inline-flex text-xs text-slate-400 hover:text-[#FF8800] transition-colors font-medium"
          >
            Founder Login
          </Link>
          <Link
            to="/contact"
            data-testid="navbar-cta-journey"
            className="hidden lg:inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold text-xs tracking-wide transition-transform duration-300 hover:scale-105"
          >
            START YOUR JOURNEY
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          <button
            data-testid="navbar-mobile-menu-button"
            className="xl:hidden text-slate-200 p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="xl:hidden glass border-t border-white/10 px-6 py-4 flex flex-col gap-3 max-h-[75vh] overflow-y-auto" data-testid="navbar-mobile-menu">
          {NAV.map((l) =>
            l.children ? (
              <div key={l.label} className="flex flex-col gap-2">
                <span className="text-[10px] font-mono-tech tracking-[0.3em] text-slate-500 uppercase pt-1">{l.label}</span>
                {l.children.map((c) => (
                  <Link
                    key={c.to}
                    to={c.to}
                    data-testid={`${c.testid}-mobile`}
                    onClick={() => setOpen(false)}
                    className="text-sm text-slate-200 py-1 pl-3"
                  >
                    {c.label}
                  </Link>
                ))}
              </div>
            ) : (
              renderLink(l, true)
            )
          )}
          <Link
            to="/contact"
            data-testid="navbar-cta-journey-mobile"
            onClick={() => setOpen(false)}
            className="mt-2 inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-full bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold text-sm"
          >
            START YOUR JOURNEY
            <ArrowRight className="w-4 h-4" />
          </Link>
        </nav>
      )}

      <div
        data-testid="scroll-progress"
        className="absolute bottom-0 left-0 h-[2px] bg-gradient-to-r from-[#FF8800] via-[#f8fafc] to-[#00E676] transition-[width] duration-150"
        style={{ width: `${progress * 100}%` }}
      />
    </header>
  );
}
